import { useCallback, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import { Style, Icon, Stroke, Fill, Text, Circle as CircleStyle } from 'ol/style';
import type { Map } from 'ol';

import {
  PIPELINE_STAGE_SEQUENCE,
  PIPELINE_STAGE_COUNT_KEY,
  PIPELINE_STAGE_VALUE_KEY,
  SALES_PIPELINE_STAGE_COLORS,
} from '../constants';
import { formatShortCurrency, getPipelineStageValue } from '../helpers/pipeline';
import { hexToRgba } from '../helpers/color';
import type { PipelineSummary } from '../views/types';
import type { SalesPipelineEntry, SalesPipelineStage } from '../../../types';

export type PipelineStyleCache = {
  single: globalThis.Map<string, Style[]>;
  cluster: globalThis.Map<string, Style>;
};

type UsePipelineStylesArgs = {
  mapInstance: MutableRefObject<Map | null>;
  pipelineStageFilter: SalesPipelineStage | 'ALL';
  pipelineSummaryRef: MutableRefObject<PipelineSummary | null>;
  pipelineEntryCountRef: MutableRefObject<number>;
};

export const usePipelineStyles = ({
  mapInstance,
  pipelineStageFilter,
  pipelineSummaryRef,
  pipelineEntryCountRef,
}: UsePipelineStylesArgs) => {
  const pipelineStyleCache = useRef<PipelineStyleCache>({
    single: new globalThis.Map<string, Style[]>(),
    cluster: new globalThis.Map<string, Style>(),
  });

  const getPipelineStageConfig = useCallback(
    (stage?: SalesPipelineStage) =>
      SALES_PIPELINE_STAGE_COLORS[stage ?? 'ENQUIRY'] ?? SALES_PIPELINE_STAGE_COLORS.ENQUIRY,
    [],
  );

  const getPipelineSingleStyle = useCallback(
    (stage: SalesPipelineStage, entry?: SalesPipelineEntry) => {
      const cache = pipelineStyleCache.current.single;
      const stageCounts = entry?.stage_counts;
      const countKey = PIPELINE_STAGE_COUNT_KEY[stage];
      const stageCountRaw = stageCounts ? Number(stageCounts[countKey] ?? 0) : 0;
      const countLabel = Number.isFinite(stageCountRaw) ? Math.round(stageCountRaw).toLocaleString() : '0';
      const amountValue = entry ? getPipelineStageValue(entry) : 0;
      const formattedShort = formatShortCurrency(amountValue);
      const valueLabel = amountValue === 0 ? '$0' : `$${formattedShort || Math.round(amountValue).toLocaleString()}`;
      let label: string;
      if (!entry) {
        label = stage === 'ENQUIRY' || stage === 'OPPORTUNITY' ? '0' : `0 (${valueLabel})`;
      } else if (stage === 'ENQUIRY' || stage === 'OPPORTUNITY') {
        label = countLabel || '0';
      } else {
        const safeCount = countLabel || '0';
        label = `${safeCount} (${valueLabel})`;
      }
      const cacheKey = `${stage}-${label}`;
      if (cache.has(cacheKey)) {
        return cache.get(cacheKey)!;
      }

      const { fill, text, shortLabel } = getPipelineStageConfig(stage);
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="32" height="80">
          <path stroke="white" stroke-width="8" fill="${fill}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
          <text x="192" y="205" text-anchor="middle" fill="${text}" font-family="Arial" font-weight="700" font-size="68">${shortLabel}</text>
        </svg>
      `;
      const markerStyle = new Style({
        image: new Icon({
          src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
          anchor: [0.5, 1],
          scale: 0.55,
        }),
      });

      const textStyle = new Style({
        text: new Text({
          text: label,
          font: '600 12px "Inter", "Arial", sans-serif',
          fill: new Fill({ color: '#0f172a' }),
          backgroundFill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
          backgroundStroke: new Stroke({ color: hexToRgba(fill, 0.35), width: 1.2 }),
          padding: [3, 8, 3, 8],
          offsetY: -50,
        }),
      });

      const arrowBorder = new Style({
        text: new Text({
          text: '\u25BC',
          font: '700 16px "Inter", "Arial", sans-serif',
          fill: new Fill({ color: hexToRgba(fill, 0.3) }),
          offsetY: -50,
        }),
      });

      const arrowFill = new Style({
        text: new Text({
          text: '\u25BC',
          font: '700 14px "Inter", "Arial", sans-serif',
          fill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
          offsetY: -32,
          textBaseline: 'bottom',
          textAlign: 'center',
        }),
      });

      const styles = [markerStyle, arrowBorder, arrowFill, textStyle];
      cache.set(cacheKey, styles);
      return styles;
    },
    [getPipelineStageConfig],
  );

  const computeClusterRadius = useCallback((size: number, zoom: number) => {
    const baseRadius = size >= 50 ? 26 : size >= 25 ? 22 : 18;
    const safeZoom = Number.isFinite(zoom) ? zoom : 10;
    let scale = 1;
    if (safeZoom < 5.5) {
      scale = 0.55;
    } else if (safeZoom < 7) {
      scale = 0.68;
    } else if (safeZoom < 8.5) {
      scale = 0.82;
    } else if (safeZoom < 10) {
      scale = 0.92;
    } else if (safeZoom > 13) {
      scale = 1.08;
    }
    return Math.max(12, Math.round(baseRadius * scale));
  }, []);

  const buildPipelineClusterStyle = useCallback(
    (stage: SalesPipelineStage, size: number, zoom: number) => {
      const cache = pipelineStyleCache.current.cluster;
      const zoomBucket = Number.isFinite(zoom) ? Math.round(zoom * 10) : 0;
      const fallbackSize = Number.isFinite(size) ? Math.max(1, Math.round(size)) : 1;
      const key = `${stage}-${fallbackSize}-${zoomBucket}`;
      if (cache.has(key)) {
        return cache.get(key)!;
      }

      const { fill, text } = getPipelineStageConfig(stage);
      const countLabel = fallbackSize.toLocaleString();
      const digitCount = countLabel.replace(/\D/g, '').length;
      let radius = computeClusterRadius(fallbackSize, zoom);
      if (digitCount >= 5) {
        radius += 8;
      } else if (digitCount === 4) {
        radius += 4;
      }

      const fontSize = digitCount >= 5 ? 11 : digitCount === 4 ? 12 : 13;

      const style = new Style({
        image: new CircleStyle({
          radius,
          fill: new Fill({ color: fill }),
          stroke: new Stroke({ color: '#ffffff', width: 2 }),
        }),
        text: new Text({
          text: countLabel,
          fill: new Fill({ color: text }),
          font: `700 ${fontSize}px "Roboto", "Helvetica", "Arial", sans-serif`,
          textBaseline: 'middle',
          textAlign: 'center',
        }),
      });
      cache.set(key, style);
      return style;
    },
    [computeClusterRadius, getPipelineStageConfig],
  );

  const pipelineClusterStyle = useCallback(
    (feature: FeatureLike): Style | Style[] => {
      const features = feature.get('features') as Feature<Geometry>[] | undefined;
      const size = features ? features.length : 0;
      if (size <= 1) {
        const singleFeature = features && features.length === 1 ? features[0] : (feature as Feature<Geometry>);
        const stage = singleFeature.get('pipelineStage') as SalesPipelineStage | undefined;
        const entry =
          (typeof (singleFeature as any).get === 'function'
            ? ((singleFeature as any).get('pipelineEntry') as SalesPipelineEntry | undefined) ??
              ((singleFeature as any).get('properties')?.pipelineEntry as SalesPipelineEntry | undefined)
            : undefined);
        return getPipelineSingleStyle(stage ?? 'ENQUIRY', entry);
      }

      const featureStageCounts: Record<SalesPipelineStage, number> = {
        ENQUIRY: 0,
        OPPORTUNITY: 0,
        QUOTATION: 0,
        SALES_ORDER: 0,
        INVOICED: 0,
      };
      const aggregatedStageCounts: Record<SalesPipelineStage, number> = {
        ENQUIRY: 0,
        OPPORTUNITY: 0,
        QUOTATION: 0,
        SALES_ORDER: 0,
        INVOICED: 0,
      };
      const aggregatedStageValues: Record<SalesPipelineStage, number> = {
        ENQUIRY: 0,
        OPPORTUNITY: 0,
        QUOTATION: 0,
        SALES_ORDER: 0,
        INVOICED: 0,
      };

      features?.forEach((f) => {
        const stage = (f.get('pipelineStage') as SalesPipelineStage | undefined) ?? 'ENQUIRY';
        featureStageCounts[stage] = (featureStageCounts[stage] ?? 0) + 1;

        const entry =
          (typeof (f as any).get === 'function'
            ? ((f as any).get('pipelineEntry') as SalesPipelineEntry | undefined) ??
              ((f as any).get('properties')?.pipelineEntry as SalesPipelineEntry | undefined)
            : undefined);
        const props =
          typeof (f as any).get === 'function'
            ? ((f as any).get('properties') as {
                pipelineCounts?: SalesPipelineEntry['stage_counts'];
                pipelineEntry?: SalesPipelineEntry;
                pipelineValues?: SalesPipelineEntry['stage_values'];
              } | null | undefined)
            : undefined;
        const stageCounts = entry?.stage_counts ?? props?.pipelineCounts;
        const stageValues = entry?.stage_values ?? props?.pipelineValues;

        if (stageCounts) {
          PIPELINE_STAGE_SEQUENCE.forEach((stageItem) => {
            const key = PIPELINE_STAGE_COUNT_KEY[stageItem];
            const rawValue = (stageCounts as SalesPipelineEntry['stage_counts'] | undefined)?.[key];
            const numeric = Number(rawValue ?? 0);
            if (Number.isFinite(numeric) && numeric > 0) {
              aggregatedStageCounts[stageItem] += numeric;
            }
          });
        } else {
          aggregatedStageCounts[stage] += 1;
        }

        if (stageValues) {
          PIPELINE_STAGE_SEQUENCE.forEach((stageItem) => {
            const valueKey = PIPELINE_STAGE_VALUE_KEY[stageItem];
            if (!valueKey) {
              return;
            }
            const rawValue = (stageValues as SalesPipelineEntry['stage_values'] | undefined)?.[valueKey];
            const numeric = Number(rawValue ?? 0);
            if (Number.isFinite(numeric) && numeric > 0) {
              aggregatedStageValues[stageItem] += numeric;
            }
          });
        }
      });

      const aggregatedEntries = Object.entries(aggregatedStageCounts).filter(([, value]) => value > 0);
      const featureEntries = Object.entries(featureStageCounts).filter(([, value]) => value > 0);

      const dominantStage =
        (aggregatedEntries.sort((a, b) => b[1] - a[1])[0]?.[0] as SalesPipelineStage | undefined) ??
        (featureEntries.sort((a, b) => b[1] - a[1])[0]?.[0] as SalesPipelineStage | undefined) ??
        'ENQUIRY';

      const zoom = mapInstance.current?.getView().getZoom() ?? 0;
      const circleCount = Number.isFinite(size) ? Math.max(1, Math.round(size)) : 1;
      const totalFeatureCount = PIPELINE_STAGE_SEQUENCE.reduce(
        (sum, stageItem) => sum + (featureStageCounts[stageItem] ?? 0),
        0,
      );

      let radius = computeClusterRadius(circleCount, zoom);
      const circleDigitCount = circleCount.toString().length;
      if (circleDigitCount >= 5) {
        radius += 8;
      } else if (circleDigitCount === 4) {
        radius += 4;
      }
      const baseStyle = buildPipelineClusterStyle(dominantStage, size, zoom);

      const targetStage = pipelineStageFilter === 'ALL' ? dominantStage : pipelineStageFilter;

      const summary = pipelineSummaryRef.current;
      const summaryAllCountRaw = summary?.totalClients;
      const summaryAllCount =
        typeof summaryAllCountRaw === 'number' && Number.isFinite(summaryAllCountRaw) ? summaryAllCountRaw : undefined;
      const summaryStageCountRaw = summary?.stageMap?.[targetStage]?.count;
      const summaryStageCount =
        typeof summaryStageCountRaw === 'number' && Number.isFinite(summaryStageCountRaw) ? summaryStageCountRaw : undefined;

      const aggregatedStageCount = aggregatedStageCounts[targetStage] ?? 0;
      const globalEntryCount = pipelineEntryCountRef.current > 0 ? pipelineEntryCountRef.current : totalFeatureCount;

      const clusterAllCount = (() => {
        if (pipelineStageFilter !== 'ALL') {
          return totalFeatureCount;
        }
        if (summaryAllCount !== undefined && summaryAllCount > 0 && globalEntryCount > 0) {
          if (totalFeatureCount >= globalEntryCount) {
            return summaryAllCount;
          }
          const scaled = Math.round((totalFeatureCount / globalEntryCount) * summaryAllCount);
          if (scaled <= 0 && totalFeatureCount > 0) {
            return 1;
          }
          return Math.min(summaryAllCount, Math.max(0, scaled));
        }
        return totalFeatureCount;
      })();

      const targetCount = (() => {
        if (pipelineStageFilter === 'ALL') {
          return clusterAllCount;
        }
        if (summaryStageCount !== undefined) {
          return aggregatedStageCount >= summaryStageCount ? summaryStageCount : aggregatedStageCount;
        }
        return aggregatedStageCount;
      })();
      const numericCount = Number.isFinite(targetCount) ? Math.max(0, Math.round(targetCount)) : 0;
      const countLabel = numericCount.toLocaleString();

      let valueLabel: string | null = null;
      if (targetStage !== 'ENQUIRY' && targetStage !== 'OPPORTUNITY') {
        let rawValue = aggregatedStageValues[targetStage] ?? 0;

        if (pipelineStageFilter === 'ALL') {
          const summaryTotalValueRaw = summary?.totalValue;
          const summaryTotalValue = Number.isFinite(summaryTotalValueRaw) ? Number(summaryTotalValueRaw) : null;

          if (summaryTotalValue !== null && summaryTotalValue > 0 && globalEntryCount > 0) {
            if (totalFeatureCount >= globalEntryCount) {
              rawValue = summaryTotalValue;
            } else {
              rawValue = Math.max(0, (totalFeatureCount / globalEntryCount) * summaryTotalValue);
            }
          } else if (targetStage !== 'INVOICED') {
            const invoiceValue = aggregatedStageValues.INVOICED ?? 0;
            if (Number.isFinite(invoiceValue) && invoiceValue > 0) {
              rawValue = invoiceValue;
            }
          }
        }

        const numericValue = Number.isFinite(rawValue) ? Number(rawValue) : 0;
        const formattedShort = formatShortCurrency(numericValue);
        valueLabel = numericValue === 0 ? '$0' : `$${formattedShort || Math.round(numericValue).toLocaleString()}`;
      }
      const labelText = valueLabel ? `${countLabel} (${valueLabel})` : countLabel;

      const { fill } = getPipelineStageConfig(targetStage);
      const labelGap = Math.max(16, Math.round(radius * 0.6));
      const labelOffset = -(radius + labelGap);
      const arrowOffset = labelOffset + Math.round(labelGap * 0.45);

      const labelStyles = [
        new Style({
          text: new Text({
            text: labelText,
            font: '600 12px "Inter", "Arial", sans-serif',
            fill: new Fill({ color: fill }),
            backgroundFill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
            backgroundStroke: new Stroke({ color: hexToRgba(fill, 0.35), width: 1.2 }),
            padding: [3, 8, 3, 8],
            offsetY: labelOffset,
            textBaseline: 'bottom',
          }),
        }),
        new Style({
          text: new Text({
            text: '\u25BC',
            font: '700 16px "Inter", "Arial", sans-serif',
            fill: new Fill({ color: hexToRgba(fill, 0.3) }),
            offsetY: arrowOffset,
          }),
        }),
        new Style({
          text: new Text({
            text: '\u25BC',
            font: '700 14px "Inter", "Arial", sans-serif',
            fill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
            offsetY: arrowOffset + 10,
            textBaseline: 'bottom',
            textAlign: 'center',
          }),
        }),
      ];

      return [baseStyle, ...labelStyles];
    },
    [
      buildPipelineClusterStyle,
      computeClusterRadius,
      getPipelineSingleStyle,
      getPipelineStageConfig,
      mapInstance,
      pipelineEntryCountRef,
      pipelineStageFilter,
      pipelineSummaryRef,
    ],
  );

  return {
    pipelineClusterStyle,
    getPipelineSingleStyle,
    computeClusterRadius,
    buildPipelineClusterStyle,
    pipelineStyleCache,
    getPipelineStageConfig,
  };
};
